import { useEffect, useState } from "react";
import { useIsMobile } from "./useIsMobile";

// Tracks the REAL visible area on mobile via window.visualViewport (iOS-safe). When the keyboard
// opens, the visual viewport shrinks even though the CSS/layout viewport (100vh, dvh, fixed) does
// not — this is why sheets/dialogs slide behind the keyboard. Reacts to keyboard open/close,
// viewport resize and orientation change.
export function useVisualViewport() {
  const read = () => {
    if (typeof window === "undefined") return { height: 0, offsetTop: 0, keyboard: 0 };
    const vv = window.visualViewport;
    if (!vv) return { height: window.innerHeight, offsetTop: 0, keyboard: 0 };
    const keyboard = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
    return { height: vv.height, offsetTop: vv.offsetTop, keyboard };
  };
  const [vp, setVp] = useState(read);
  useEffect(() => {
    const vv = window.visualViewport;
    const on = () => setVp(read());
    on();
    if (vv) { vv.addEventListener("resize", on); vv.addEventListener("scroll", on); }
    window.addEventListener("orientationchange", on);
    return () => {
      if (vv) { vv.removeEventListener("resize", on); vv.removeEventListener("scroll", on); }
      window.removeEventListener("orientationchange", on);
    };
  }, []);
  return vp;
}

// Keyboard-safe inline style for a centered Radix DialogContent on mobile: top-anchored to the
// visible viewport and capped to its height so the body scrolls and the footer/CTA stay reachable
// above the keyboard. Returns undefined on desktop (no override — desktop stays unchanged).
export function useMobileDialogStyle() {
  const isMobile = useIsMobile();
  const vp = useVisualViewport();
  if (!isMobile) return undefined;
  return {
    top: (vp.offsetTop || 0) + 8,
    bottom: "auto",
    transform: "translateX(-50%)",
    maxHeight: vp.height ? vp.height - 16 : undefined,
  };
}
